import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { JwtHelperService } from './jwt-helper.service';
import { StorageService } from './storage.service';
import { LoginRequest, User } from '../../shared';

// API Response interface matching your backend
export interface AuthResponse {
  token: string;
  email: string;
  userId: string;
  expiresAt: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiUrl = environment.apiUrl;
  private readonly tokenKey = 'access_token';
  private readonly userKey = 'user_data';
  private redirectUrl: string | null = null;

  // Signals for reactive state management
  private currentUserSignal = signal<User | null>(null);
  private isLoadingSignal = signal<boolean>(false);

  // Computed signals
  readonly currentUser = computed(() => this.currentUserSignal());
  readonly isAuthenticated = computed(() => !!this.getToken() && !this.isTokenExpired());
  readonly isLoading = computed(() => this.isLoadingSignal());

  constructor(
    private http: HttpClient,
    private router: Router,
    private jwtHelper: JwtHelperService,
    private storage: StorageService
  ) {
    this.initializeAuthState();
  }

  private initializeAuthState(): void {
    const token = this.getToken();

    if (token && !this.jwtHelper.isTokenExpired(token)) {
      // Restore user from storage
      const storedUser = this.storage.getItem<User>(this.userKey);
      if (storedUser) {
        this.currentUserSignal.set(storedUser);
      } else {
        // User data missing, rebuild from token
        this.rebuildUserFromToken(token);
      }
    } else if (token) {
      // Token exists but expired
      this.clearAuthData();
    }
  }

  private rebuildUserFromToken(token: string): void {
    const decoded = this.jwtHelper.decodeToken(token);
    if (decoded) {
      const user: User = {
        id: decoded.UserId || '',
        email: decoded.email || '',
        firstName: decoded.EnglishName?.split(' ')[0] || '',
        lastName: decoded.EnglishName?.split(' ').slice(1).join(' ') || '',
        fullName: decoded.EnglishName || decoded.ArabicName || '',
        role: decoded.Role as any || 'user',
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      this.storage.setItem(this.userKey, user);
      this.currentUserSignal.set(user);
    }
  }

  login(credentials: LoginRequest): Observable<AuthResponse> {
    this.isLoadingSignal.set(true);

    return this.http.post<AuthResponse>(`${this.apiUrl}api/Auth/login`, {
      email: credentials.email,
      password: credentials.password
    }).pipe(
      tap((response) => {
        this.setAuthData(response);
        this.isLoadingSignal.set(false);
      }),
      catchError((error) => {
        this.isLoadingSignal.set(false);
        return throwError(() => error);
      })
    );
  }

  logout(): void {
    this.clearAuthData();
    this.router.navigate(['/auth/login']);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  getCurrentUser(): User | null {
    return this.currentUserSignal();
  }

  getUserEmail(): string | null {
    const user = this.currentUserSignal();
    return user?.email || null;
  }

  setRedirectUrl(url: string): void {
    this.redirectUrl = url;
  }

  getRedirectUrl(): string | null {
    const url = this.redirectUrl;
    this.redirectUrl = null;
    return url;
  }

  private setAuthData(response: AuthResponse): void {
    // Store token in localStorage
    localStorage.setItem(this.tokenKey, response.token);

    // Extract user info from JWT token for richer data
    const decoded = this.jwtHelper.decodeToken(response.token);

    // Create user object from response and token data
    const user: User = {
      id: response.userId || decoded?.UserId || '',
      email: response.email || decoded?.email || '',
      firstName: decoded?.EnglishName?.split(' ')[0] || '',
      lastName: decoded?.EnglishName?.split(' ').slice(1).join(' ') || '',
      fullName: decoded?.EnglishName || decoded?.ArabicName || response.email.split('@')[0],
      role: (decoded?.Role as any) || 'user',
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Store user data
    this.storage.setItem(this.userKey, user);
    this.currentUserSignal.set(user);
  }

  private clearAuthData(): void {
    localStorage.removeItem(this.tokenKey);
    this.storage.removeItem(this.userKey);
    this.currentUserSignal.set(null);
  }

  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) {
      return true;
    }
    return this.jwtHelper.isTokenExpired(token);
  }

  // Get user roles from token
  getUserRoles(): string[] {
    const token = this.getToken();
    if (!token) {
      return [];
    }
    return this.jwtHelper.getRolesFromToken(token);
  }

  // Check if user has a specific role
  hasRole(role: string): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    return this.jwtHelper.hasRole(token, role);
  }

  // Check if user has any of the specified roles
  hasAnyRole(roles: string[]): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    return this.jwtHelper.hasAnyRole(token, roles);
  }
}
